import { useMemo, useState, useContext } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { authLabels } from "@/i18n";
import { getLanguage } from "@/utils/languageUtils";
import { AuthService } from "@/services/auth/authService";
import { AuthContext } from "@/context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CircleHelp } from "lucide-react";
import {
  openBankIdInSystemBrowser,
  resolveApiAbsoluteUrl,
} from "@/lib/native/bankIdBrowser";
import { isNativeCapacitorApp } from "@/integrations/clients/carAuction/nativeAuth";

interface BankIdLoginDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  redirect?: string;
}

const BankIdLoginDialog = ({
  open,
  onOpenChange,
  redirect,
}: BankIdLoginDialogProps) => {
  const authContext = useContext(AuthContext);
  const queryClient = useQueryClient();
  const labels = useMemo(() => authLabels(getLanguage()), []);
  const bankIdLabels = labels.login?.bankId ?? {};
  const help = bankIdLabels.help ?? {};
  const helpSteps: string[] = Array.isArray(help.steps) ? help.steps : [];
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [helpValue, setHelpValue] = useState("");

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setIsSubmitting(false);
      setHelpValue("");
    }
    onOpenChange(nextOpen);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      try {
        await AuthService.logout();
      } catch (logoutError) {
        console.warn("[BankID login] logout API failed (continuing)", logoutError);
      }

      authContext?.logout();
      queryClient.clear();
      const { resetNativeAuthClientState } = await import(
        "@/integrations/clients/carAuction/carAuctionClient"
      );
      resetNativeAuthClientState();

      const response = await AuthService.startBankIdLogin(redirect);
      if (isNativeCapacitorApp() && response.continueUrl) {
        await openBankIdInSystemBrowser(response.continueUrl);
        setIsSubmitting(false);
        onOpenChange(false);
        return;
      }

      const path = response.redirectUrl.startsWith("/")
        ? response.redirectUrl
        : `/${response.redirectUrl}`;
      window.location.replace(resolveApiAbsoluteUrl(path));
    } catch (error: any) {
      console.error("[BankID login] failed", error);
      toast.error(
        error?.message ||
          bankIdLabels.error ||
          "BankID-inloggning misslyckades.",
      );
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <div className="space-y-5">
          <DialogHeader className="space-y-3">
            <DialogTitle className="flex items-center gap-2">
              <img
                src="/assets/BankID_logo.png"
                alt="BankID"
                className="h-6 w-auto object-contain"
              />
              {bankIdLabels.title || "Logga in med BankID"}
            </DialogTitle>
            <DialogDescription className="rounded-md border border-primary/25 bg-primary/10 px-3 py-2.5 text-sm font-medium text-foreground">
              {bankIdLabels.description}
            </DialogDescription>
          </DialogHeader>

          <Button
            type="button"
            className="w-full"
            disabled={isSubmitting}
            onClick={() => void handleSubmit()}
          >
            {isSubmitting
              ? bankIdLabels.starting || "Öppnar BankID..."
              : bankIdLabels.continue || "Fortsätt med BankID"}
          </Button>

          <Accordion
            type="single"
            collapsible
            value={helpValue}
            onValueChange={setHelpValue}
            className="rounded-md border border-border bg-muted/30 px-3"
          >
            <AccordionItem value="help" className="border-b-0">
              <AccordionTrigger className="py-3 text-left text-sm font-medium hover:no-underline">
                <span className="flex items-center gap-2">
                  <CircleHelp className="h-4 w-4 shrink-0 text-muted-foreground" />
                  {help.title || "Hur verifierar jag mitt konto med BankID?"}
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-3 pt-0 text-sm text-muted-foreground space-y-2.5">
                <p>{help.intro}</p>
                <ol className="list-decimal space-y-1 pl-4 text-foreground/90">
                  {helpSteps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
                <p>{help.after}</p>
                {help.createWithBankId ? (
                  <p className="text-sm font-medium text-foreground">
                    {help.createWithBankId}
                  </p>
                ) : null}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BankIdLoginDialog;
